// A ledger line's source document, shown as its PDF over the screen (owner 2026-10-08: "that is what
// the source document must drill down to" — the PDF that is sent, not the editing form). Like the bank
// statement viewer (statementview.js), it opens on top of the screen, so closing it leaves you on the
// same line, same place.
//   · a sales document (invoice, credit note) shows its own print: the PDF the email carries
//     (GET /documents/{id}/pdf → report_export_service.document_pdf);
//   · a purchase document (supplier invoice, goods received, supplier return) shows the supplier's own
//     file when one is attached — the earliest PDF or picture on it — and otherwise our print of it.
//   const doc = documentViewer(companyId);
//   doc.open({ document_id, type_code, reference });
//   ${() => doc.view()}                                 // once, in a template built once
import { html, signal } from './t4.js';
import { api } from './api.js';
import { t } from './i18n.js';

const PURCHASE = new Set(['SINV', 'GRV', 'RTS', 'PORD']);
const SHOWABLE = /^(application\/pdf|image\/)/i;

export function documentViewer(companyId){
  const P = p => '/companies/' + companyId + p;
  const state = signal(null);   // {status:'loading'|'error'|'ready', title, url, kind:'pdf'|'image', error}
  let seq = 0;                  // guards the async loads against a closed / re-opened viewer

  function close(){
    seq++;
    const s = state.value;
    if(s && s.url){ try { URL.revokeObjectURL(s.url); } catch { /* already revoked */ } }
    state.value = null;
  }

  // the supplier's own file on a purchase document: the earliest PDF or picture attached to it
  async function supplierFile(documentId){
    let r;
    try { r = await api.get(P('/documents/' + documentId + '/attachments')); } catch { return null; }
    const files = ((r && r.data) || []).filter(a => SHOWABLE.test(a.mime || ''));
    files.sort((a, b) => String(a.uploaded_at || '').localeCompare(String(b.uploaded_at || '')) || a.id - b.id);
    return files[0] || null;
  }

  async function open(line){
    const id = line && line.document_id;
    if(!id) return;
    const my = ++seq;
    const title = line.reference || t('report.gl.document', 'Document');
    state.value = { status: 'loading', title };
    let path = P('/documents/' + id + '/pdf'), kind = 'pdf';
    if(PURCHASE.has(line.type_code)){
      const f = await supplierFile(id);
      if(my !== seq) return;
      if(f){ path = P('/attachments/' + f.id); kind = /^image\//i.test(f.mime || '') ? 'image' : 'pdf'; }
    }
    let url = '';
    try { url = await api.objectUrl(path); }
    catch(e){
      if(my === seq) state.value = { status: 'error', title,
        error: (e.data && e.data.error) || t('report.gl.no_pdf_or_document', 'No PDF or document available') };
      return;
    }
    if(my !== seq){ try { URL.revokeObjectURL(url); } catch { /* stale */ } return; }
    state.value = { status: 'ready', title, url, kind };
  }

  function modal(s){
    return html`<div class="cb-stmt-overlay" @click=${e => { if(e.target === e.currentTarget) close(); }}>
      <div class="cb-stmt-modal doc-pdf-modal" role="dialog" aria-label=${t('report.gl.source_document', 'Source document')}>
        <div class="cb-stmt-head">
          <span class="fw-700 cb-stmt-title">${s.title}</span>
          <span class="cb-mode-spacer"></span>
          <button class="cb-clear-btn" title=${t('common.close', 'Close')} @click=${close}>✕</button>
        </div>
        <div class="cb-stmt-body doc-pdf-body">
          ${s.status === 'loading'
            ? html`<div class="cb-stmt-locating">${t('report.gl.loading_document', 'Opening the document…')}</div>`
            : s.status === 'error'
              ? html`<div class="muted-block cb-stmt-err">${s.error}</div>`
              : s.kind === 'image'
                ? html`<img class="cb-stmt-img" src=${s.url} alt=${s.title}>`
                : html`<iframe class="doc-pdf-frame" src=${s.url} title=${s.title}></iframe>`}
        </div>
      </div>
    </div>`;
  }

  return { open, close, state, view: () => (state.value ? modal(state.value) : []) };
}
